/* "Kurss" product template (RedDigital, 2026-10).
   One <ton-buybox> per page: variant tiers, extra-mix choices, add-on, price chip, delivery
   estimate, sticky bar, and add-to-cart that reports through Canyon's cart event bus so the
   theme's cart drawer and cart count update exactly as with the theme's own product form. */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const formatMoney = (cents, format) => {
  const fmt = format || '{{amount_with_comma_separator}} €';
  const value = (Number(cents) || 0) / 100;
  const parts = (decimals, thousands, decimal) => {
    const [int, frac] = value.toFixed(decimals).split('.');
    const withSep = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return frac ? withSep + decimal + frac : withSep;
  };
  return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount': return parts(2, ',', '.');
      case 'amount_no_decimals': return parts(0, ',', '.');
      case 'amount_with_comma_separator': return parts(2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return parts(0, '.', ',');
      case 'amount_with_space_separator': return parts(2, ' ', ',');
      case 'amount_with_apostrophe_separator': return parts(2, "'", '.');
      default: return parts(2, ',', '.');
    }
  }).replace(/ (?=€)|(?<=€) /g, ' '); // keep the € on the same line as the number
};

class TonBuybox extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    const data = this.querySelector('script[data-ton-data]');
    this.data = data ? JSON.parse(data.textContent) : { variants: [] };
    this.state = { variantId: Number(this.dataset.selected) || this.data.variants[0]?.id, addons: new Set(), mixes: {} };

    this.tiers = [...this.querySelectorAll('[data-ton-tier]')];
    this.tiers.forEach((el) => el.addEventListener('click', () => this.selectVariant(Number(el.dataset.variantId))));
    this.addEventListener('keydown', (e) => this.onRadioKeys(e));

    this.querySelectorAll('[data-ton-mix]').forEach((group) => {
      const key = group.dataset.property;
      const first = group.querySelector('[data-ton-mix-opt][aria-checked="true"]');
      if (first) this.state.mixes[key] = first.dataset.value.trim();
      group.querySelectorAll('[data-ton-mix-opt]').forEach((opt) =>
        opt.addEventListener('click', () => {
          group.querySelectorAll('[data-ton-mix-opt]').forEach((o) => o.setAttribute('aria-checked', String(o === opt)));
          this.state.mixes[key] = opt.dataset.value.trim();
        })
      );
    });

    this.querySelectorAll('[data-ton-addon]').forEach((el) =>
      el.addEventListener('click', () => {
        const id = Number(el.dataset.addonId);
        const on = !this.state.addons.has(id);
        on ? this.state.addons.add(id) : this.state.addons.delete(id);
        el.setAttribute('aria-checked', String(on));
        this.render();
      })
    );

    this.chip = this.querySelector('[data-ton-pricechip]');
    this.breakdown = this.querySelector('[data-ton-breakdown]');
    this.chip?.addEventListener('click', () => {
      const open = this.chip.getAttribute('aria-expanded') !== 'true';
      this.chip.setAttribute('aria-expanded', String(open));
      if (this.breakdown) this.breakdown.hidden = !open;
    });

    this.querySelectorAll('[data-ton-atc]').forEach((b) => b.addEventListener('click', () => this.addToCart(b)));
    document.querySelectorAll('[data-ton-sticky-atc]').forEach((b) => b.addEventListener('click', () => this.addToCart(b)));

    this.initGallery();
    this.initDelivery();
    this.initSticky();
    this.render();
  }

  get variant() {
    return this.data.variants.find((v) => v.id === this.state.variantId) || this.data.variants[0];
  }

  money(cents) {
    return formatMoney(cents, this.data.moneyFormat);
  }

  perDay(v) {
    const days = v.days > 0 ? v.days : (this.data.daysPerUnit || 0) * (v.units || 1);
    return days > 0 ? Math.round(v.price / days) : null;
  }

  selectVariant(id) {
    const v = this.data.variants.find((x) => x.id === id);
    if (!v || !v.available) return;
    this.state.variantId = id;
    this.render();
    if (this.data.updateUrl) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', id);
      window.history.replaceState({}, '', url);
    }
  }

  onRadioKeys(e) {
    const current = e.target.closest?.('[data-ton-tier]');
    if (!current || !['ArrowDown', 'ArrowUp', 'ArrowRight', 'ArrowLeft'].includes(e.key)) return;
    e.preventDefault();
    const enabled = this.tiers.filter((t) => !t.disabled);
    const i = enabled.indexOf(current);
    const next = enabled[(i + (e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1 : -1) + enabled.length) % enabled.length];
    next.focus();
    this.selectVariant(Number(next.dataset.variantId));
  }

  render() {
    const v = this.variant;
    if (!v) return;
    const addonPrice = [...this.querySelectorAll('[data-ton-addon]')]
      .filter((el) => this.state.addons.has(Number(el.dataset.addonId)))
      .reduce((sum, el) => sum + Number(el.dataset.addonPrice || 0), 0);
    const total = v.price + addonPrice;
    const day = this.perDay(v);

    this.tiers.forEach((t) => {
      const on = Number(t.dataset.variantId) === v.id;
      t.setAttribute('aria-checked', String(on));
      t.tabIndex = on ? 0 : -1;
    });
    this.querySelectorAll('[data-ton-mix]').forEach((g) => {
      g.hidden = (v.units || 1) < Number(g.dataset.minUnits || 2);
    });
    const set = (sel, text) => document.querySelectorAll(sel).forEach((el) => (el.textContent = text));
    set(`[data-ton-for="${this.id}"][data-ton-price]`, this.money(v.price));
    set(`[data-ton-for="${this.id}"][data-ton-total]`, this.money(total));
    set(`[data-ton-for="${this.id}"][data-ton-day]`, day != null ? this.money(day) : '');
    set(`[data-ton-for="${this.id}"][data-ton-name]`, v.name);
    document.querySelectorAll(`[data-ton-for="${this.id}"][data-ton-compare]`).forEach((el) => {
      el.textContent = v.compare > v.price ? this.money(v.compare) : '';
      el.hidden = !(v.compare > v.price);
    });
    document.querySelectorAll(`[data-ton-for="${this.id}"][data-ton-day-wrap]`).forEach((el) => (el.hidden = day == null));
    const soldOut = !v.available;
    document.querySelectorAll(`[data-ton-for="${this.id}"][data-ton-atc-label]`).forEach((el) => {
      el.textContent = soldOut ? el.dataset.soldOut : el.dataset.label;
    });
    [...this.querySelectorAll('[data-ton-atc]'), ...document.querySelectorAll('[data-ton-sticky-atc]')].forEach((b) => (b.disabled = soldOut));
  }

  /* ---------- Add to cart ---------- */
  async addToCart(button) {
    const v = this.variant;
    if (!v || !v.available || this.busy) return;
    this.busy = true;
    button.setAttribute('aria-busy', 'true');
    const error = this.querySelector('[data-ton-error]');
    if (error) error.hidden = true;

    const properties = {};
    this.querySelectorAll('[data-ton-mix]').forEach((g) => {
      const key = g.dataset.property;
      if (!g.hidden && this.state.mixes[key]) properties[key] = this.state.mixes[key];
    });
    const items = [{ id: v.id, quantity: 1, properties }];
    this.state.addons.forEach((id) => items.push({ id, quantity: 1 }));

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const root = window.Shopify?.routes?.root || '/';
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const json = await res.json();
      if (!res.ok || json.status) throw new Error(json.description || json.message || 'Add to cart failed');

      const cart = await fetch(`${root}cart.js`).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'ton-buybox',
          sourceId: this.id,
          itemCount: items.reduce((n, i) => n + i.quantity, 0),
          productId: this.dataset.productId,
          sections: json.sections,
          didError: false,
        },
      });

      const drawer = document.querySelector('cart-drawer-component');
      const mode = this.data.afterAdd || 'auto';
      if (mode === 'cart' || (mode === 'auto' && !drawer)) {
        window.location.href = this.data.cartUrl || `${root}cart`;
      } else if (drawer && mode !== 'stay') {
        const td = drawer.closest('theme-drawer');
        if (td && !td.isOpen && typeof td.open === 'function') td.open();
      }
    } catch (e) {
      deferred.reject(e);
      this.dispatchEvent(new CartErrorEvent({ error: e.message, code: 'INVALID' }));
      if (error) {
        error.textContent = e.message;
        error.hidden = false;
      }
    } finally {
      this.busy = false;
      button.removeAttribute('aria-busy');
    }
  }

  /* ---------- Gallery ---------- */
  initGallery() {
    const track = this.querySelector('[data-ton-track]');
    if (!track) return;
    const slides = [...track.children];
    const dots = [...this.querySelectorAll('[data-ton-dot]')];
    const thumbs = [...this.querySelectorAll('[data-ton-thumb]')];
    const go = (i) => track.scrollTo({ left: slides[i].offsetLeft - track.offsetLeft, behavior: 'smooth' });
    [...dots, ...thumbs].forEach((b) => b.addEventListener('click', () => go(Number(b.dataset.index))));
    const mark = () => {
      const i = Math.round(track.scrollLeft / (track.clientWidth || 1));
      [...dots, ...thumbs].forEach((b) => b.setAttribute('aria-current', String(Number(b.dataset.index) === i)));
    };
    track.addEventListener('scroll', () => requestAnimationFrame(mark), { passive: true });
  }

  /* ---------- Delivery estimate ---------- */
  initDelivery() {
    this.querySelectorAll('[data-ton-delivery]').forEach((el) => {
      const cutoff = Number(el.dataset.cutoff ?? 14);
      const minDays = Number(el.dataset.min ?? 1);
      const maxDays = Number(el.dataset.max ?? minDays);
      const workdays = (el.dataset.workdays || '1,2,3,4,5').split(',').map(Number);
      const tz = el.dataset.timezone || undefined;
      const now = new Date(new Date().toLocaleString('en-US', { timeZone: tz }));
      const addWorkdays = (from, n) => {
        const d = new Date(from);
        let added = 0;
        while (added < n) {
          d.setDate(d.getDate() + 1);
          if (workdays.includes(d.getDay())) added++;
        }
        return d;
      };
      let start = new Date(now);
      if (!workdays.includes(now.getDay()) || now.getHours() >= cutoff) start = addWorkdays(now, 1);
      const locale = el.dataset.locale || document.documentElement.lang || 'lv';
      const names = (el.dataset.weekdays || '').split(',').map((x) => x.trim()).filter(Boolean);
      const fmt = (d) => {
        const date = d.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
        const day = names.length === 7 ? names[d.getDay()] : d.toLocaleDateString(locale, { weekday: 'long' });
        return `${day}, ${date}`;
      };
      const a = addWorkdays(start, minDays);
      const b = addWorkdays(start, maxDays);
      const text = maxDays > minDays ? `${fmt(a)} – ${fmt(b)}` : fmt(a);
      el.querySelectorAll('[data-ton-date]').forEach((s) => (s.textContent = text));
      el.querySelectorAll('[data-ton-cutoff]').forEach((s) => (s.textContent = `${String(cutoff).padStart(2, '0')}:00`));
    });
  }

  /* ---------- Sticky add-to-cart bar ---------- */
  initSticky() {
    const bar = document.querySelector(`[data-ton-sticky][data-ton-for="${this.id}"]`);
    const offer = this.querySelector('[data-ton-offer]') || this.querySelector('[data-ton-atc]');
    if (!bar || !offer) return;
    const footer = document.querySelector('footer, .shopify-section-group-footer-group');
    const check = () => {
      const passed = offer.getBoundingClientRect().bottom < 0;
      const atFooter = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
      const show = passed && !atFooter;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', String(!show));
      bar.inert = !show;
    };
    window.addEventListener('scroll', () => requestAnimationFrame(check), { passive: true });
    check();
  }
}

if (!customElements.get('ton-buybox')) customElements.define('ton-buybox', TonBuybox);

// Smooth-scroll for in-page links such as #buy, #reviews, #faq
document.addEventListener('click', (e) => {
  const a = e.target.closest?.('a[href^="#"]');
  if (!a || a.getAttribute('href').length < 2) return;
  const target = document.getElementById(a.getAttribute('href').slice(1));
  if (!target) return;
  e.preventDefault();
  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
});
